import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Button, Icon, Label, Message } from 'semantic-ui-react';
import styled from 'styled-components';
import {
  CritiqueGroup,
  CritiqueGroupVisibility,
  useUpdateGroupVisibility,
} from 'src/hooks/useCritiqueGroups';
import useCanHostCritiqueGroups from './useCanHostCritiqueGroups';
import { TierGateModal } from 'src/components/critiqueGroups/tierGate';

/**
 * Host-only "Who can join?" panel — private ↔ link-only ↔ public.
 *
 * A sibling Panel to the feedback-visibility one, NOT a section inside it:
 * joining and feedback-visibility are different concepts, they save to
 * different endpoints, and the two share nothing but styling.
 *
 * Paid gate: for a FREE host the two public options render disabled with a
 * "Premium" pill; clicking one opens the upgrade modal instead of selecting it.
 * The gate is presentational — the server enforces it, and this ALSO handles
 * UPGRADE_REQUIRED_PUBLIC_GROUP coming back (a plan can lapse mid-session, so
 * `canHost` being true is never a guarantee).
 */

const Panel = styled.div`
  background: var(--br-surface);
  border: 1px solid #e7e3d9;
  border-radius: 14px;
  overflow: hidden;
  max-width: 620px;
  margin-bottom: 18px;
`;

const Head = styled.div`
  padding: 18px 24px;
  border-bottom: 1px solid #eee7da;
  font-weight: 800;
  font-size: 17px;
`;

const Body = styled.div`
  padding: 24px;
`;

const Foot = styled.div`
  padding: 14px 24px;
  border-top: 1px solid #eee7da;
  background: var(--br-raised-surface);
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
`;

const Option = styled.div<{ $on: boolean; $locked?: boolean }>`
  border: ${({ $on }) => ($on ? '2px solid #00b5ad' : '1.5px solid #e7e3d9')};
  background: ${({ $on }) => ($on ? '#f2fbfa' : '#fff')};
  border-radius: 12px;
  padding: 13px 15px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  opacity: ${({ $locked }) => ($locked ? 0.7 : 1)};
`;

const Radio = styled.span<{ $on: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 4px solid ${({ $on }) => ($on ? '#00b5ad' : '#d0cabb')};
  margin-top: 2px;
  flex-shrink: 0;
`;

const OptionTitle = styled.div`
  font-weight: 700;
  font-size: 13.5px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const OptionHelp = styled.div`
  font-size: 12px;
  color: #8a857b;
  margin-top: 4px;
`;

type Choice = {
  value: CritiqueGroupVisibility;
  title: string;
  help: string;
  paid: boolean;
  testid: string;
};

const VisibilityPanel: React.FC<{ group: CritiqueGroup }> = ({ group }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { canHost } = useCanHostCritiqueGroups();
  const save = useUpdateGroupVisibility(group._id);

  const [visibility, setVisibility] = useState<CritiqueGroupVisibility>(
    group.visibility
  );
  const [saved, setSaved] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);

  useEffect(() => {
    setVisibility(group.visibility);
  }, [group.visibility]);

  const choices: Choice[] = [
    {
      value: 'invite_only',
      title: t('CritiquePrivate', 'Private'),
      help: t(
        'CritiquePrivateHelp',
        'Only people you add can see or join this group.'
      ),
      paid: false,
      testid: 'visibility-invite-only',
    },
    {
      value: 'request',
      title: t('CritiqueLinkOnly', 'Link-only'),
      help: t(
        'CritiqueLinkOnlyHelp',
        'Anyone with the link can see the group and ask to join. Not listed publicly.'
      ),
      paid: true,
      testid: 'visibility-request',
    },
    {
      value: 'listed',
      title: t('CritiquePublic', 'Public'),
      help: t(
        'CritiquePublicHelp',
        'Listed in the discovery catalog so writers can find your group and ask to join.'
      ),
      paid: true,
      testid: 'visibility-listed',
    },
  ];

  const pick = (choice: Choice) => {
    // A free host who reaches for a paid option gets the upgrade prompt instead
    // of a selection — show the value, lock the payoff.
    if (choice.paid && !canHost) {
      setGateOpen(true);
      return;
    }
    setSaved(false);
    setVisibility(choice.value);
  };

  const dirty = visibility !== group.visibility;

  const submit = () => {
    setSaved(false);
    save.mutate(
      { visibility },
      {
        onSuccess: () => setSaved(true),
        onError: err => {
          // Belt-and-braces: the server is the authority, so honour a late
          // upgrade requirement even if `canHost` said otherwise.
          if (err.code === 'UPGRADE_REQUIRED_PUBLIC_GROUP') {
            setVisibility(group.visibility);
            setGateOpen(true);
          }
        },
      }
    );
  };

  return (
    <>
      <Panel data-testid="visibility-settings">
        <Head>{t('CritiqueWhoCanJoin', 'Who can join?')}</Head>
        <Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {choices.map(choice => {
              const on = visibility === choice.value;
              const locked = choice.paid && !canHost;
              return (
                <Option
                  key={choice.value}
                  $on={on}
                  $locked={locked}
                  onClick={() => pick(choice)}
                  data-testid={choice.testid}
                >
                  <Radio $on={on} />
                  <div style={{ flex: 1 }}>
                    <OptionTitle>
                      {choice.title}
                      {locked && (
                        <Label
                          size="mini"
                          color="orange"
                          data-testid={`${choice.testid}-premium`}
                        >
                          <Icon name="gem" style={{ margin: '0 4px 0 0' }} />
                          {t('Premium', 'Premium')}
                        </Label>
                      )}
                    </OptionTitle>
                    <OptionHelp>{choice.help}</OptionHelp>
                  </div>
                </Option>
              );
            })}
          </div>

          {save.isError &&
            save.error?.code !== 'UPGRADE_REQUIRED_PUBLIC_GROUP' && (
              <Message
                negative
                size="small"
                data-testid="visibility-error"
                style={{ marginTop: 14 }}
              >
                {save.error?.message}
              </Message>
            )}
        </Body>
        <Foot>
          {saved && !dirty && (
            <span style={{ fontSize: 12.5, color: '#2fa84f', fontWeight: 700 }}>
              {t('CritiqueSettingsSaved', 'Saved')}
            </span>
          )}
          <Button
            color="teal"
            onClick={submit}
            disabled={!dirty || save.isLoading}
            loading={save.isLoading}
            style={{ borderRadius: 9, fontWeight: 700 }}
            data-testid="visibility-save"
          >
            {t('CritiqueSaveSettings', 'Save settings')}
          </Button>
        </Foot>
      </Panel>

      <TierGateModal
        open={gateOpen}
        onClose={() => setGateOpen(false)}
        onUpgrade={() => history.push('/account/subscription')}
        limit="public"
        serverMessage={
          save.error?.code === 'UPGRADE_REQUIRED_PUBLIC_GROUP'
            ? save.error?.message
            : undefined
        }
      />
    </>
  );
};

export default VisibilityPanel;
